import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Container, Typography } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataModelConfiguration } from "../../../../api/types/dataModelConfigurationTypes";
import useClientContext from "../../../../context/useClientContext";
import AssignClientToConfigurationDialog from "./AssignClientToConfigurationDialog";
import ConfigurationsGrid from "./ConfigurationsGrid";
import { ConfigurationsGridActionsContextProvider } from "./ConfigurationsGridActionsContextProvider";
import CreateConfigurationDialog from "./CreateConfigurationDialog";
import DeleteConfigurationDialog from "./DeleteConfigurationDialog";
import EditConfigurationDialog from "./EditConfigurationDialog";
import type { ClientConfigurationAssignment, ClientConfigurationListState } from "./configurationsListState";

const ConfigurationsList = () => {
  const { hasPermissions } = useClientContext();

  const [state, setState] = useState<ClientConfigurationListState>({});

  const [configurations, error, { isFetching, setData: setConfigurations }] = useFetch(
    adminApi.getDataModelConfigurations
  );

  if (error) {
    logError(error, "[ConfigurationsList]");
    return <DataLoadingFailed title="Failed to load configurations" />;
  }

  const hasEditPermissions = hasPermissions(["ManageOrganizationDataModel"]);

  const handleAddConfiguration = () => {
    setState({ openDialog: "create_configuration" });
  };

  const handleRenameConfiguration = (id: string) => {
    const editedConfiguration = (configurations ?? []).find((config) => config.id === id);
    if (editedConfiguration) {
      setState({ openDialog: "edit_configuration", editedConfiguration });
    }
  };

  const handleDeleteConfiguration = (id: string) => {
    const deletedConfiguration = (configurations ?? []).find((config) => config.id === id);
    if (deletedConfiguration) {
      setState({ openDialog: "delete_configuration", deletedConfiguration });
    }
  };

  const handleAssignClient = (clientAssignment: ClientConfigurationAssignment) => {
    setState({ openDialog: "assign_client", clientAssignment });
  };

  const handleConfigurationCreated = (newConfiguration: DataModelConfiguration) => {
    setState({});
    setConfigurations((prevConfigurations) =>
      prevConfigurations ? [...prevConfigurations, newConfiguration] : [newConfiguration]
    );
  };

  const handleConfigurationUpdated = (updatedConfiguration: DataModelConfiguration) => {
    setState({});
    setConfigurations((prevConfigurations) =>
      prevConfigurations
        ? prevConfigurations.map((config) => (config.id === updatedConfiguration.id ? updatedConfiguration : config))
        : undefined
    );
  };

  const handleConfigurationDeleted = (deletedConfiguration: DataModelConfiguration) => {
    setState({});
    setConfigurations((prevConfigurations) =>
      prevConfigurations ? prevConfigurations.filter((config) => config.id !== deletedConfiguration.id) : undefined
    );
  };

  const handleClientAssigned = (clientAssignment: ClientConfigurationAssignment) => {
    setState({});
    setConfigurations((prevConfigurations) =>
      prevConfigurations
        ? prevConfigurations.map((config) => {
            if (config.id === clientAssignment.toConfiguration.id) {
              return { ...config, clientCodes: [...config.clientCodes, clientAssignment.clientCode] };
            }
            if (config.id === clientAssignment.fromConfiguration.id) {
              return {
                ...config,
                clientCodes: config.clientCodes.filter((code) => code !== clientAssignment.clientCode),
              };
            }
            return config;
          })
        : undefined
    );
  };

  return (
    <ConfigurationsGridActionsContextProvider
      configurations={configurations ?? []}
      onRenameConfiguration={handleRenameConfiguration}
      onDeleteConfiguration={handleDeleteConfiguration}
      onAssignClient={handleAssignClient}
    >
      <Container maxWidth="xl" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {hasEditPermissions && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
            <Typography color="textSecondary">
              Create company configurations to customize object type layouts.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddConfiguration}>
              Add Configuration
            </Button>
          </Box>
        )}

        <ConfigurationsGrid
          configurations={configurations ?? []}
          loading={isFetching}
          hasEditPermissions={hasEditPermissions}
        />
      </Container>
      {state.openDialog === "create_configuration" && (
        <CreateConfigurationDialog onClose={() => setState({})} onCreated={handleConfigurationCreated} />
      )}
      {state.openDialog === "edit_configuration" && state.editedConfiguration && (
        <EditConfigurationDialog
          editedConfiguration={state.editedConfiguration}
          onClose={() => setState({})}
          onUpdated={handleConfigurationUpdated}
        />
      )}
      {state.openDialog === "delete_configuration" && state.deletedConfiguration && (
        <DeleteConfigurationDialog
          deletedConfiguration={state.deletedConfiguration}
          onClose={() => setState({})}
          onDeleted={handleConfigurationDeleted}
        />
      )}
      {state.openDialog === "assign_client" && state.clientAssignment && (
        <AssignClientToConfigurationDialog
          assignment={state.clientAssignment}
          onClose={() => setState({})}
          onAssigned={handleClientAssigned}
        />
      )}
    </ConfigurationsGridActionsContextProvider>
  );
};

export default ConfigurationsList;
